<script lang="ts" setup>
import { formatProductForModification } from '@/utils/formatProductForModification'

import { useProductCounter } from '@/composables/useProductCounter'

import { useCityStore } from '@/store/city/city'

import { Item } from '@/types/api/api-v3/basket'

import AppMeasureUnits from '@/components/App/AppMeasureUnits.vue'

type Props = {
  item: Item
}

const props = defineProps<Props>()

const { currencySymbol } = useCityStore()

const productId = computed(() => props.item?.product_id || 0)

const productModification = formatProductForModification(props.item)

const {
  quantitySelectedProducts,
  setProductQuantity,
  decreaseQuantity,
  increaseQuantity,
} = useProductCounter(productId, productModification)

const productImage = props.item?.image_url
  ? props.item?.image_url
  : './no-image-category.webp'

const priceWithSymbol = computed(() => {
  return `${props?.item?.price * props?.item?.quantity} ${currencySymbol}`
})

watch(
  () => props.item?.quantity,
  (updatedQuantity) => setProductQuantity(updatedQuantity || 0),
)

onMounted(() => setProductQuantity(props.item?.quantity || 0))
</script>

<template>
  <div class="product">
    <div class="product__image">
      <nuxt-img :src="productImage" format="webp" alt="изображение продукта" />
    </div>

    <div class="product__info">
      <nuxt-link class="product__info-link" :to="item?.url" target="_blank">
        {{ item.title }}
      </nuxt-link>

      <div class="product__info-attributes">
        <div class="price">{{ priceWithSymbol }}</div>

        <app-measure-units
          v-if="item?.total_weight"
          class="weight"
          :weight="item.total_weight"
          :volume-in-liters="item?.volume_in_liters"
          :measure="item.measure"
        />
      </div>
    </div>

    <div class="product__info-counter">
      <the-counter
        :value="quantitySelectedProducts"
        :min="0"
        @on-increase="increaseQuantity"
        @on-decrease="decreaseQuantity"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.product {
  display: grid;
  grid-template-columns: 60px 1fr max-content;
  gap: 16px;
  align-items: center;

  &__image {
    width: 60px;
    min-width: 60px;
    height: 40px;

    img {
      width: 100%;
      border-radius: 10px;
    }
  }

  &__info {
    font-size: 14px;
    font-weight: 600;
    word-break: break-all;

    &-link {
      color: var(--ui-text-primary);
      word-break: break-word;

      &:hover {
        color: var(--ui-primary);
      }
    }

    &-attributes {
      display: flex;
      gap: 8px;
      align-items: center;

      .price {
        font-weight: 800;
        color: var(--ui-text-primary);
      }

      .weight {
        font-size: 12px;
        color: var(--ui-text-secondary);
      }
    }
  }

  @include media('>tablet') {
    grid-template-columns: 75px 1fr max-content;

    &__image {
      width: 75px;
      min-width: 75px;
      height: auto;
    }

    &__info {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &-link {
        padding-right: 16px;
      }

      &-attributes {
        display: grid;
        gap: 4px;
        width: 97px;
        min-width: 97px;
      }
    }
  }
}
</style>
